import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from 'context/auth/auth_context';
import axiosInstance from 'utils/axios-instance';
import Swal from 'sweetalert2';
import './request.css';

type Community = {
    uuid: string;
    name: string;
    description: string;
    type: string;
    geoFeaturesName: string;
}

const AdminRequestPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const authContext = useContext(AuthContext);
    const { isAuthenticated } = authContext;
    const [loading, setLoading] = useState(false);
    const [community, setCommunity] = useState<Community | null>(null);
    const [hasAdmin, setHasAdmin] = useState(false);
    const [requestMessage, setRequestMessage] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            checkStatusAndLoadData();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated, id]);

    const checkStatusAndLoadData = async () => {

        try {
            setLoading(true);

            // 커뮤니티 정보 조회 (건물/동네 자동 판별)
            let communityResponse;
            try {
                communityResponse = await axiosInstance.get(`/api/community/building?uuid=${id}`);
            } catch {
                communityResponse = await axiosInstance.get(`/api/community/town?uuid=${id}`);
            }
            if (communityResponse.data.statusCode === 200) {
                setCommunity(communityResponse.data.community);
            }

            // 관리자 권한 확인
            const adminCheckResponse = await axiosInstance.get(
                `/api/community/admin/check?communityUuid=${id}`
            );

            // 이미 관리자인 경우 커뮤니티 메인 페이지로 리다이렉트 (무한루프 방지)
            if (adminCheckResponse.data) {
                Swal.fire({
                    icon: 'info',
                    title: '이미 관리자입니다',
                    text: '커뮤니티 페이지로 이동합니다.',
                }).then(() => {
                    navigate(`/community/building/${id}`);
                });
                return;
            }

            // 관리자 존재 여부 확인
            const hasAdminResponse = await axiosInstance.get(
                `/api/community/admin/has-admin?communityUuid=${id}`
            );
            setHasAdmin(hasAdminResponse.data);

        } catch (error) {
            console.error('데이터 로드 실패:', error);
            Swal.fire({
                icon: 'error',
                title: '오류',
                text: '데이터를 불러오는 중 오류가 발생했습니다.',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!requestMessage || requestMessage.trim().length < 10) {
            Swal.fire({
                icon: 'warning',
                title: '입력 오류',
                text: '신청 메시지를 10자 이상 입력해주세요.',
            });
            return;
        }

        try {
            setSubmitting(true);
            const response = await axiosInstance.post('/api/community/admin/request', {
                communityUuid: id,
                requestMessage: requestMessage.trim()
            });

            if (response.data.statusCode === 200) {
                Swal.fire({
                    icon: 'success',
                    title: '신청 완료',
                    text: hasAdmin 
                        ? '관리자 신청이 완료되었습니다. 기존 관리자의 승인을 기다려주세요.' 
                        : '관리자 신청이 완료되었습니다. 웹 운영자의 승인을 기다려주세요.',
                }).then(() => {
                    navigate(`/community/building/${id}`);
                });
            }
        } catch (error: any) {
            console.error('관리자 신청 실패:', error);
            const errorMessage = error.response?.data?.message || '관리자 신청 중 오류가 발생했습니다.';
            
            if (error.response?.status === 409) {
                Swal.fire({
                    icon: 'warning',
                    title: '이미 신청됨',
                    text: errorMessage,
                }).then(() => {
                    navigate(`/community/building/${id}`);
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: '오류',
                    text: errorMessage,
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="admin-request-page">
                <div className="loading-container">
                    <div className="loader"></div>
                    <p>로딩 중...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return (
            <div className="admin-request-page">
                <div className="request-container">
                    <div className="request-header">
                        <h1>관리자 신청</h1>
                    </div>
                    <div className="request-info-box">
                        <h3>📋 로그인이 필요합니다</h3>
                        <p>관리자 신청을 하려면 로그인이 필요합니다.</p>
                    </div>
                    <div className="form-actions">
                        <button 
                            className="submit-button"
                            onClick={() => navigate('/auth/signin', {
                                state: {
                                    from: `/community/${id}/settings/request`,
                                    fromfrom: `/community/${id}/settings/request`
                                }
                            })}
                        >
                            로그인하기
                        </button>
                        <button 
                            className="cancel-button"
                            onClick={() => navigate(`/community/building/${id}`)}
                        >
                            취소
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-request-page">
            <div className="request-container">
                <div className="request-header">
                    <h1>관리자 신청</h1>
                    {community && (
                        <p className="community-info">
                            {community.name} ({community.geoFeaturesName})
                        </p>
                    )}
                </div>

                <div className="request-info-box">
                    {hasAdmin ? (
                        <>
                            <h3>📋 관리자 신청 안내</h3>
                            <ul>
                                <li>이 커뮤니티에는 이미 관리자가 있습니다.</li>
                                <li>신청 후 기존 관리자의 승인이 필요합니다.</li>
                                <li>관리자가 승인하면 관리자 권한을 획득합니다.</li>
                                <li>성실하고 책임감 있게 커뮤니티를 관리할 의지를 보여주세요.</li>
                            </ul>
                        </>
                    ) : (
                        <>
                            <h3>📋 관리자 신청 안내</h3>
                            <ul>
                                <li>이 커뮤니티는 아직 관리자가 없습니다.</li>
                                <li>신청 후 웹 운영자의 검토를 거쳐 승인됩니다.</li>
                                <li>승인되면 관리자 권한을 획득합니다.</li>
                                <li>커뮤니티를 적극적으로 관리할 의지를 보여주세요.</li>
                            </ul>
                        </>
                    )}
                </div>

                <form onSubmit={handleSubmit} className="request-form">
                    <div className="form-group">
                        <label>신청 메시지 *</label>
                        <textarea
                            placeholder="왜 관리자가 되고 싶은지, 어떻게 커뮤니티를 관리할 계획인지 자유롭게 작성해주세요. (최소 10자)"
                            value={requestMessage}
                            onChange={(e) => setRequestMessage(e.target.value)}
                            rows={8}
                            required
                            minLength={10}
                        />
                        <span className="char-count">{requestMessage.length} / 1000</span>
                    </div>

                    <div className="form-actions">
                        <button 
                            type="submit" 
                            className="submit-button"
                            disabled={submitting || requestMessage.trim().length < 10}
                        >
                            {submitting ? '신청 중...' : '신청하기'}
                        </button>
                        <button 
                            type="button" 
                            className="cancel-button"
                            onClick={() => navigate(`/community/building/${id}`)}
                            disabled={submitting}
                        >
                            취소
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminRequestPage;

